import axios from "axios";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import useOutsideClick from "../../../../../../hooks/useOutsideClick";
import { MdOutlineClose } from "react-icons/md";
import { BiDotsVerticalRounded } from "react-icons/bi";
import { RiSendPlane2Line } from "react-icons/ri";
import moment from "moment";

import { CommentContainer } from "../../styled/scheduler";

// 리사이즈 파라미터 안전하게 붙이기
const withResize = (url) => {
  if (!url) return "";
  if (url.includes("resize=true")) return url;
  return url.includes("?") ? `${url}&resize=true` : `${url}?resize=true`;
};

const Comment = ({
  eventInfo,
  comments,
  getComment,
  isShared = false,
  isProject = false,
  setImageOrder = null,
  setIsDetail = null,
  getImages = null,
}) => {
  const contextRef = useRef();

  const user = useSelector((state) => state?.user?.data[0]);
  const { isDarkMode } = useSelector((state) => state.scheduler);

  const [active, setActive] = useState(null);
  const [formData, setFormData] = useState({
    content: "",
    delYn: "N",
  });
  const [grouped, setGrouped] = useState({});

  useEffect(() => {
    setGrouped(splitComments(comments));
  }, [comments]);

  useOutsideClick(contextRef, () => {
    setActive(null);
  });

  const editComment = (item) => {
    setFormData(item);
    setActive(null);
  };

  const submitComment = async (data) => {
    const url = "/api/scheduleComment/post";
    const body = {
      ...formData,
      ...data,
      scheduleNo: eventInfo.scheduleNo,
      mberNo: user.mberNo,
      createMberId: user.userName,
      updateMberId: user.userName,
    };

    if (!body.content) {
      toast.error("댓글을 입력해주세요.");
      return;
    }

    try {
      const res = await axios.post(url, body);

      if (res.status === 200) {
        toast.success(
          `댓글이 ${body.delYn === "Y" ? "삭제" : body.id ? "수정" : "작성"}되었습니다.`,
        );
        await getComment();
        setFormData({ content: "", delYn: "N" });
        setActive(null);

        // 사진댓글과 연동된 상태면 갤러리도 갱신
        if (body.photoReviewNo && getImages) {
          await getImages();
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const splitComments = (comments) => {
    if (!comments) return {};
    const dateGroupedComments = {};

    comments.forEach((comment) => {
      const dateKey = comment.createDt.split(" ")[0];
      if (!dateGroupedComments[dateKey]) {
        dateGroupedComments[dateKey] = [];
      }
      dateGroupedComments[dateKey].push(comment);
    });

    return dateGroupedComments;
  };

  // 사진댓글 클릭 시 "상세(뷰어)"로: MediaViewer를 띄우는 구조 유지
  // - 프리뷰는 resize=true
  // - MediaViewer에는 원본(full url)을 path로 넘김
  const handleImageClick = useCallback(
    (item) => {
      if (!setImageOrder || !setIsDetail) return;
      if (!item?.photoPath) return;

      const originUrl = imgHandler(item.photoPath); // 상대경로 → 원본 full url

      setImageOrder([
        {
          createNm: item.mberNm,
          refeNo: item.scheduleNo,
          path: originUrl, // MediaViewer에서는 원본으로 표시
          photoNo: item.photoNo,
          createDt: item.createDt,
        },
      ]);
      setIsDetail(true);
    },
    [setImageOrder, setIsDetail],
  );

  return (
    <CommentContainer className={`comment ${isProject ? "" : isDarkMode}`}>
      <div className="box">
        {comments && comments.length > 0 ? (
          Object.entries(grouped).map(([date, items], index) => (
            <React.Fragment key={date}>
              <p className="date" style={{ zIndex: index + 1 }}>
                <span>{moment(date).format("YYYY년 MM월 DD일")}</span>
              </p>
              <ul>
                {items.map((item) => {
                  // 프로필/사진댓글 미리보기는 resize=true로 가볍게
                  const profileSrc = item.imgPath
                    ? withResize(imgHandler(item.imgPath))
                    : "/img/user2.png";

                  const photoPreviewSrc = item.photoPath
                    ? withResize(imgHandler(item.photoPath))
                    : "";

                  return (
                    <li key={item.id} className={item.copy ? "copy" : ""}>
                      <img
                        src={profileSrc}
                        alt={item.mberNm}
                        loading="lazy"
                        decoding="async"
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />

                      <div className="comment">
                        <span>{item.mberNm}</span>
                        <span>
                          {item.copy && "(복사) "}
                          {item.photoPath && "(사진댓글) "}
                          {moment(item.createDt).format("LT")}
                        </span>

                        <p style={{ display: "flex", alignItems: "flex-start" }}>
                          {item.photoPath && (
                            <img
                              src={photoPreviewSrc}
                              alt=""
                              loading="lazy"
                              decoding="async"
                              onClick={() => handleImageClick(item)}
                              style={{
                                width: 104, // 프리뷰 크기 키움
                                height: 104,
                                borderRadius: 10,
                                objectFit: "cover",
                                cursor: "pointer",
                                marginRight: 10,
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <span>{item.content}</span>
                        </p>
                      </div>

                      {item.mberNo === user?.mberNo && !isShared && (
                        <div className="dropdown">
                          <button onClick={() => setActive(item.id)}>
                            <BiDotsVerticalRounded />
                          </button>
                          {active === item.id && (
                            <ul ref={contextRef}>
                              {!item.copy && <li onClick={() => editComment(item)}>수정</li>}
                              <li onClick={() => submitComment({ ...item, delYn: "Y" })}>삭제</li>
                            </ul>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </React.Fragment>
          ))
        ) : (
          <div className="not">댓글이 존재하지 않습니다.</div>
        )}
      </div>

      {!isShared && (
        <div className="form">
          <input
            type="text"
            value={formData.content}
            onChange={(e) => setFormData((data) => ({ ...data, content: e.target.value }))}
          />

          {formData.content && formData.content.length > 0 && (
            <button
              className="reset"
              onClick={() => {
                setFormData({ content: "", delYn: "N" });
                setActive(null);
              }}
            >
              <MdOutlineClose />
            </button>
          )}

          <button className="submit" onClick={() => submitComment(formData)}>
            <RiSendPlane2Line />
          </button>
        </div>
      )}
    </CommentContainer>
  );
};

export default Comment;
